import enrollPatientIntoRegistryAction from '@app/s/registry/actions/enrollPatientIntoRegistryAction'
import usePostTrialApplyStepViewedEvent from '@components/hooks/usePostTrialApplyStepViewedEvent'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { EllipsesPulse } from '@components/primitives/LoadingWrapper/EllipsesPulse'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import { type ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import debounce from 'lodash/debounce'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { getApplySuccessRouteToRedirectTo } from '../ConsentToShareEmrStep/getApplySuccessRouteToRedirectTo'

export function PatientRegistryGuardianPrompt({
  consentForEmr,
  consentToShareYourStory,
  hasPatientNavigatorAgentCallCompleted,
  idToken,
  patientNavigatorAgentId,
  patientRegistryEnrolled,
  screeningVisitAvailabilities,
  whyAreYouApplying,
}: Pick<
  ApplySuccessPageProps,
  | 'consentForEmr'
  | 'consentToShareYourStory'
  | 'hasPatientNavigatorAgentCallCompleted'
  | 'idToken'
  | 'patientNavigatorAgentId'
  | 'patientRegistryEnrolled'
  | 'screeningVisitAvailabilities'
  | 'whyAreYouApplying'
>) {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const goToNextScreen = () => {
    router.push(
      getApplySuccessRouteToRedirectTo('guardian', {
        consentForEmr,
        consentToShareYourStory,
        hasPatientNavigatorAgentCallCompleted,
        idToken,
        patientNavigatorAgentId,
        patientRegistryEnrolled,
        screeningVisitAvailabilities,
        whyAreYouApplying,
      }),
    )
  }

  usePostTrialApplyStepViewedEvent({
    trialApplicationIdToken: idToken,
    type: 'registry-guardian-enrollment',
  })

  const handleSelfEnrollment = useCallback(async () => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    await enrollPatientIntoRegistryAction({
      patientRegistryEnrolledAs: 'Self',
      trialApplicationIdToken: idToken,
    })
    setIsSubmitting(false)

    trackEvent(TrackingEvents.PATIENT_REGISTRY_ENROLLMENT_SELF_CLICKED, {
      source: TrackingSources.TRIAL_APPLICATION_FORM,
    })

    goToNextScreen()
    return
  }, [isSubmitting, setIsSubmitting])
  const debouncedHandleSelfEnrollment = debounce(handleSelfEnrollment, 500)

  const handleGuardianEnrollment = useCallback(async () => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      await enrollPatientIntoRegistryAction({
        patientRegistryEnrolledAs: 'Guardian',
        trialApplicationIdToken: idToken,
      })
    } finally {
      setIsSubmitting(false)

      trackEvent(TrackingEvents.PATIENT_REGISTRY_ENROLLMENT_GUARDIAN_CLICKED, {
        source: TrackingSources.TRIAL_APPLICATION_FORM,
      })

      goToNextScreen()
    }
  }, [isSubmitting, setIsSubmitting])

  const debouncedHandleGuardianEnrollment = debounce(
    handleGuardianEnrollment,
    500,
  )

  return (
    <div className='mx-auto max-w-3xl'>
      <div className='grid grid-cols-1 gap-6'>
        <div className='grid grid-cols-1 gap-4'>
          <Text element='h1' styleName='h3'>
            Are you registering for yourself, or for someone else as their
            guardian?
          </Text>
          <Text className='text-neutral500' element='h2' styleName='p-small'>
            Please note that individuals under the age of 18 are unable to
            register for themselves.
          </Text>
        </div>

        <Button
          className={classMerge(
            buttonClassNames.legacy.outline.black,
            'h-auto w-full items-center rounded-lg',
          )}
          onClick={debouncedHandleSelfEnrollment}
          value={
            <div className='p-4'>
              {isSubmitting ? (
                <EllipsesPulse ellipsesColor='bg-black' />
              ) : (
                <p>
                  <Text className='inline' element='span' styleName='p-strong'>
                    I am registering for myself,&nbsp;
                  </Text>
                  <Text className='inline' element='span' styleName='p'>
                    and am at least 18 years old.
                  </Text>
                </p>
              )}
            </div>
          }
        />
        <Button
          className={classMerge(
            buttonClassNames.legacy.outline.black,
            'h-auto w-full items-center rounded-lg',
          )}
          onClick={debouncedHandleGuardianEnrollment}
          value={
            <div className='p-4'>
              <p>
                <Text className='inline' element='span' styleName='p-strong'>
                  I am registering for someone else,&nbsp;
                </Text>
                <Text className='inline' element='span' styleName='p'>
                  as their legally authorized representative or guardian.
                </Text>
              </p>
            </div>
          }
        />
      </div>
    </div>
  )
}
